// (c) chaehongee. Confidential and proprietary.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ChatMessage, InteractiveState } from '../../components/Backstage/utils/types'

export interface ContextUsage {
    model: string
    inputTokens: number
    outputTokens: number
    outputTokenLimit: number
    contextWindowLimit: number
}

export interface ChatState {
    messages: ChatMessage[]
    input: string
    isProcessing: boolean
    interactive: InteractiveState
    contextUsage: ContextUsage | null
}

interface ChatSliceState {
    bySessionId: Record<string, ChatState>
}

const initialChatState = (): ChatState => ({
    messages: [],
    input: '',
    isProcessing: false,
    interactive: null,
    contextUsage: null,
})

const initialState: ChatSliceState = { bySessionId: {} }

function getChatState(state: ChatSliceState, sessionId: string): ChatState {
    if (!state.bySessionId[sessionId]) {
        state.bySessionId[sessionId] = initialChatState()
    }
    return state.bySessionId[sessionId]
}

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setMessages: (
            state,
            action: PayloadAction<{ sessionId: string; messages: ChatMessage[] }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            s.messages = action.payload.messages
        },
        appendMessage: (
            state,
            action: PayloadAction<{ sessionId: string; message: ChatMessage }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            s.messages.push(action.payload.message)
        },
        updateLastMessageChunk: (
            state,
            action: PayloadAction<{ sessionId: string; chunk: string }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            const lastMsg = s.messages[s.messages.length - 1]
            if (lastMsg && lastMsg.kind === 'bubble' && lastMsg.role === 'assistant') {
                lastMsg.text += action.payload.chunk
            }
        },
        updateLastMessageNodeIds: (
            state,
            action: PayloadAction<{ sessionId: string; nodeIds: string[] }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            const lastMsg = s.messages[s.messages.length - 1]
            if (lastMsg && lastMsg.kind === 'bubble') {
                const existing = new Set(lastMsg.nodeIds || [])
                action.payload.nodeIds.forEach((id) => existing.add(id))
                lastMsg.nodeIds = Array.from(existing)
            }
        },
        setLastMessagePending: (
            state,
            action: PayloadAction<{ sessionId: string; pending: boolean }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            const lastMsg = s.messages[s.messages.length - 1]
            if (lastMsg && lastMsg.kind === 'bubble') {
                lastMsg.pending = action.payload.pending
            }
        },
        clearAllPending: (state, action: PayloadAction<{ sessionId: string }>) => {
            const s = getChatState(state, action.payload.sessionId)
            s.messages.forEach((msg) => {
                if (msg.kind === 'bubble') {
                    msg.pending = false
                }
            })
        },
        setInput: (state, action: PayloadAction<{ sessionId: string; input: string }>) => {
            const s = getChatState(state, action.payload.sessionId)
            s.input = action.payload.input
        },
        setIsProcessing: (
            state,
            action: PayloadAction<{ sessionId: string; isProcessing: boolean }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            s.isProcessing = action.payload.isProcessing
        },
        setInteractive: (
            state,
            action: PayloadAction<{ sessionId: string; interactive: InteractiveState }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            s.interactive = action.payload.interactive
        },
        setContextUsage: (
            state,
            action: PayloadAction<{ sessionId: string; contextUsage: ContextUsage | null }>
        ) => {
            const s = getChatState(state, action.payload.sessionId)
            s.contextUsage = action.payload.contextUsage
        },
        resetChat: (state, action: PayloadAction<{ sessionId: string }>) => {
            state.bySessionId[action.payload.sessionId] = initialChatState()
        },
        removeChatState: (state, action: PayloadAction<{ sessionId: string }>) => {
            delete state.bySessionId[action.payload.sessionId]
        },
    },
})

export const {
    setMessages,
    appendMessage,
    updateLastMessageChunk,
    updateLastMessageNodeIds,
    setLastMessagePending,
    clearAllPending,
    setInput,
    setIsProcessing,
    setInteractive,
    setContextUsage,
    resetChat,
    removeChatState,
} = chatSlice.actions

export default chatSlice.reducer

// Selectors
export const selectChatMessages = (state: any, sessionId: string) =>
    (state.chat?.bySessionId?.[sessionId]?.messages ?? []) as ChatMessage[]
export const selectChatInput = (state: any, sessionId: string) =>
    (state.chat?.bySessionId?.[sessionId]?.input ?? '') as string
export const selectIsProcessing = (state: any, sessionId: string) =>
    Boolean(state.chat?.bySessionId?.[sessionId]?.isProcessing)
export const selectInteractive = (state: any, sessionId: string) =>
    (state.chat?.bySessionId?.[sessionId]?.interactive ?? null) as InteractiveState
export const selectContextUsage = (state: any, sessionId: string) =>
    (state.chat?.bySessionId?.[sessionId]?.contextUsage ?? null) as ContextUsage | null
